import { getCookie } from '../CookieStorageService'
import { ClientConfig } from '../../config/ClientConfig'

function readCookieNumber(name: string): number | null {
	const cookie = getCookie(name)
	if (!cookie?.value) return null
	const num = Number(cookie.value)
	return Number.isFinite(num) && num >= 0 ? num : null
}

function readCookieBool(name: string): boolean | null {
	const cookie = getCookie(name)
	if (!cookie?.value) return null
	return cookie.value === 'true'
}

export const GeoConfig = {
	sdkLoadTimeoutMs(): number {
		return readCookieNumber('GEO_SDK_TIMEOUT_MS')
			?? ClientConfig.GEO_SDK_TIMEOUT_MS
	},

	locateDeviceTimeoutMs(): number {
		return readCookieNumber('GEO_LOCATE_DEVICE_TIMEOUT_MS')
			?? ClientConfig.GEO_LOCATE_DEVICE_TIMEOUT_MS
	},

	callbackFailedThreshold(): number {
		return readCookieNumber('GEO_CALLBACK_FAILED_THRESHOLD')
			?? ClientConfig.GEO_CALLBACK_FAILED_THRESHOLD
	},

	callbackRestrictedThreshold(): number {
		return readCookieNumber('GEO_CALLBACK_RESTRICTED_THRESHOLD')
			?? ClientConfig.GEO_CALLBACK_RESTRICTED_THRESHOLD
	},

	callbackAllowedThreshold(): number {
		return readCookieNumber('GEO_CALLBACK_ALLOWED_THRESHOLD')
			?? ClientConfig.GEO_CALLBACK_ALLOWED_THRESHOLD
	},

	customGuidanceEnabled(): boolean {
		return readCookieBool('GEO_CUSTOM_GUIDANCE_ENABLED')
			?? (ClientConfig as Record<string, unknown>).GEO_CUSTOM_GUIDANCE_ENABLED === true
	},

	modalTimeoutMs(): number {
		return readCookieNumber('GEO_MODAL_TIMEOUT_MS') ?? 300_000
	},

	casinoGracePeriodMs(): number {
		return readCookieNumber('GEO_CASINO_GRACE_PERIOD_MS') ?? 30_000
	},

	signoutDelaySystemMs(): number {
		return readCookieNumber('GEO_SIGNOUT_DELAY_SYSTEM_MS') ?? 10_000
	},

	signoutDelayPlayerMs(): number {
		return readCookieNumber('GEO_SIGNOUT_DELAY_PLAYER_MS') ?? 3_000
	},

	/** Minimum time a player must wait between manual location recheck attempts. */
	manualRecheckCooldownMs(): number {
		return readCookieNumber('GEO_MANUAL_RECHECK_COOLDOWN_MS') ?? 60_000
	},

	/** undefined means no limit: a hold lasts until protection ends or the location resolves. */
	holdTimeLimitMs(): number | undefined {
		const value = readCookieNumber('GEO_HOLD_TIME_LIMIT_MS')
			?? ClientConfig.GEO_HOLD_TIME_LIMIT_MS
		return value === 0 ? undefined : value
	},

	canaryPercent(): number {
		return readCookieNumber('GEO_COMPLY_CANARY_PERCENT')
			?? ((ClientConfig as Record<string, unknown>).GEO_COMPLY as { canaryPercent: number } | undefined)?.canaryPercent
			?? 0
	},

	/** Returns raw ClientConfig defaults (no cookie override). For debug UI hints. */
	getConfigDefaults(): {
		sdkTimeoutMs: number
		locateDeviceTimeoutMs: number
		callbackFailedThreshold: number
		callbackRestrictedThreshold: number
		callbackAllowedThreshold: number
		canaryPercent: number
		modalTimeoutMs: number
		signoutDelaySystemMs: number
		holdTimeLimitMs: number | undefined
	} {
		const geoComply = (ClientConfig as Record<string, unknown>).GEO_COMPLY as { canaryPercent: number } | undefined
		return {
			sdkTimeoutMs: ClientConfig.GEO_SDK_TIMEOUT_MS,
			locateDeviceTimeoutMs: ClientConfig.GEO_LOCATE_DEVICE_TIMEOUT_MS,
			callbackFailedThreshold: ClientConfig.GEO_CALLBACK_FAILED_THRESHOLD,
			callbackRestrictedThreshold: ClientConfig.GEO_CALLBACK_RESTRICTED_THRESHOLD,
			callbackAllowedThreshold: ClientConfig.GEO_CALLBACK_ALLOWED_THRESHOLD,
			canaryPercent: geoComply?.canaryPercent ?? 0,
			modalTimeoutMs: 300_000,
			signoutDelaySystemMs: 10_000,
			holdTimeLimitMs: ClientConfig.GEO_HOLD_TIME_LIMIT_MS,
		}
	},
}
